import { useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { useOperatorsList, useRolesList, useStartImpersonation } from '../../api/queries';

/**
 * ACES-only "act as operator" picker — two steps:
 *   1. Pick the operator to assume.
 *   2. Pick one of THAT operator's active operator-tier roles.
 *
 * Impersonation is a full role swap: while it's active the actor's ARMS
 * role is dropped and permissions become exactly the chosen role's. The
 * actor (sub) stays the real user, so every audit row records both who
 * acted and which role they assumed. Re-impersonating mid-session is not
 * supported (starting needs the ARMS permission, which is dropped) — you
 * stop first.
 *
 * Returns a fresh JWT carrying the impersonation claims; we swap it into
 * sessionStorage and reload the window so every cached query re-fetches
 * under the new tenancy + role context.
 */
export function ImpersonateOperatorDialog({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey, queryFn: operators.listFn, enabled: open,
  });
  const start = useStartImpersonation();

  const [operatorId, setOperatorId] = useState('');
  const [roleId, setRoleId] = useState('');

  // Roles for the chosen operator. The ACES-only ?operatorId= escape hatch
  // surfaces that operator's clones; we then keep only active operator-tier
  // roles (a platform / supplier / inactive role can't be assumed).
  const roles = useRolesList(operatorId || null);
  const rolesQ = useQuery({
    queryKey: roles.queryKey, queryFn: roles.listFn, enabled: open && !!operatorId,
  });
  const assumableRoles = (rolesQ.data ?? []).filter(
    (r) => r.isActive && r.tier === 'Operator' && r.operatorId === operatorId,
  );

  function onOperatorChange(next: string) {
    setOperatorId(next);
    // Clear the role whenever the operator changes — a role from the
    // previous operator must never carry across.
    setRoleId('');
  }

  async function go() {
    if (!operatorId || !roleId) return;
    // useStartImpersonation's onSuccess stores the new token (before it
    // invalidates queries) so refetches carry the impersonation claims.
    await start.mutateAsync({ operatorId, roleId });
    // Full reload — easiest way to invalidate every cached query and
    // theme branding under the new operator + role scope.
    window.location.assign('/home');
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Impersonate operator
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick the operator and the role you want to act as. For the
            session your ARMS access is dropped — you hold exactly the
            chosen role&apos;s permissions, scoped to that operator. Every
            audit row still records your real user id. Stop impersonating to
            return to ARMS.
          </Typography>
          <TextField
            select label="Operator" value={operatorId}
            onChange={(e) => onOperatorChange(e.target.value)} fullWidth
            disabled={operatorsQ.isLoading}
            helperText={operatorsQ.isLoading ? 'Loading operators…' : 'Required.'}
          >
            {(operatorsQ.data ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>
                {op.displayName} ({op.code})
              </MenuItem>
            ))}
            {(!operatorsQ.isLoading && (operatorsQ.data?.length ?? 0) === 0) && (
              <MenuItem disabled value="">No operators yet.</MenuItem>
            )}
          </TextField>
          <TextField
            select label="Role" value={roleId}
            onChange={(e) => setRoleId(e.target.value)} fullWidth
            disabled={!operatorId || rolesQ.isLoading}
            helperText={
              !operatorId ? 'Pick an operator first.'
                : rolesQ.isLoading ? 'Loading roles…'
                  : assumableRoles.length === 0 ? 'This operator has no assumable roles.'
                    : 'Required.'
            }
          >
            {assumableRoles.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.displayName} ({r.code})
              </MenuItem>
            ))}
            {(!!operatorId && !rolesQ.isLoading && assumableRoles.length === 0) && (
              <MenuItem disabled value="">No roles available.</MenuItem>
            )}
          </TextField>
          {start.error && (
            <ErrorAlert>
              {(start.error as { message?: string }).message ?? 'Failed to start impersonation.'}
            </ErrorAlert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={start.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!operatorId || !roleId || start.isPending} onClick={go}>
          {start.isPending ? 'Starting…' : 'Start impersonation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
